/* HC - size chart frames (blocks/hc-size-charts.liquid). A chart wider than its box
   scrolls sideways; this gives that scroll a face: the ‹ › buttons in the chart's bar,
   mouse dragging, and data-hc-overflow on the frame (none / start / middle / end), which
   the block's CSS turns into the edge fade and the pinned column's shadow. Touch and
   trackpad scrolling stay native. */

class HcSizeChartFrame extends HTMLElement {
  /** @type {HTMLElement | null} */
  #scroller = null;

  #frame = 0;

  /** @type {{ id: number, x: number, left: number, moved: boolean } | null} */
  #drag = null;

  #resizeObserver = new ResizeObserver(() => this.#update());

  // A section re-render (morph) resets attributes to the server's markup; restate them.
  #attributeObserver = new MutationObserver(() => {
    if (!this.hasAttribute('data-hc-overflow')) this.#update();
  });

  connectedCallback() {
    const scroller = this.querySelector('.hc-size-chart__scroll');
    if (!(scroller instanceof HTMLElement)) return;
    this.#scroller = scroller;

    scroller.addEventListener('scroll', this.#onScroll, { passive: true });
    scroller.addEventListener('pointerdown', this.#onPointerDown);
    scroller.addEventListener('pointermove', this.#onPointerMove);
    scroller.addEventListener('pointerup', this.#onPointerEnd);
    scroller.addEventListener('pointercancel', this.#onPointerEnd);
    scroller.addEventListener('dragstart', this.#onDragStart);
    this.addEventListener('click', this.#onClick);

    this.#resizeObserver.observe(scroller);
    if (scroller.firstElementChild) this.#resizeObserver.observe(scroller.firstElementChild);
    this.#attributeObserver.observe(this, { attributes: true, attributeFilter: ['data-hc-overflow'] });
    this.#update();
  }

  disconnectedCallback() {
    const scroller = this.#scroller;
    if (scroller) {
      scroller.removeEventListener('scroll', this.#onScroll);
      scroller.removeEventListener('pointerdown', this.#onPointerDown);
      scroller.removeEventListener('pointermove', this.#onPointerMove);
      scroller.removeEventListener('pointerup', this.#onPointerEnd);
      scroller.removeEventListener('pointercancel', this.#onPointerEnd);
      scroller.removeEventListener('dragstart', this.#onDragStart);
    }
    this.removeEventListener('click', this.#onClick);
    this.#resizeObserver.disconnect();
    this.#attributeObserver.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#scroller = null;
    this.#drag = null;
  }

  #onScroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  };

  #update() {
    const scroller = this.#scroller;
    if (!scroller) return;

    const max = scroller.scrollWidth - scroller.clientWidth;
    const left = Math.abs(scroller.scrollLeft);
    const state = max <= 1 ? 'none' : left <= 1 ? 'start' : left >= max - 1 ? 'end' : 'middle';
    if (this.dataset.hcOverflow !== state) this.dataset.hcOverflow = state;

    for (const button of this.querySelectorAll('[data-hc-chart-step]')) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const back = Number(button.dataset.hcChartStep) < 0;
      const off = state === 'none' || state === (back ? 'start' : 'end');
      if (button.disabled !== off) button.disabled = off;
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-hc-chart-step]') : null;
    const scroller = this.#scroller;
    if (!(button instanceof HTMLElement) || !scroller) return;

    // A step is most of the columns in view, so one column stays on screen for context.
    const pinned = scroller.querySelector('tr > :first-child');
    const view = scroller.clientWidth - (pinned ? pinned.getBoundingClientRect().width : 0);
    scroller.scrollBy({ left: Number(button.dataset.hcChartStep) * Math.max(view * 0.8, 80), behavior: 'smooth' });
  };

  // Mouse only: touch and pens already pan natively.
  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (!this.#scroller || this.dataset.hcOverflow === 'none') return;
    this.#drag = { id: event.pointerId, x: event.clientX, left: this.#scroller.scrollLeft, moved: false };
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    const drag = this.#drag;
    const scroller = this.#scroller;
    if (!drag || !scroller || event.pointerId !== drag.id) return;

    const dx = event.clientX - drag.x;
    if (!drag.moved) {
      // Under 4px it is a click or the start of a text selection, not a drag.
      if (Math.abs(dx) < 4) return;
      drag.moved = true;
      scroller.setPointerCapture(drag.id);
      scroller.dataset.hcDragging = '';
      window.getSelection()?.removeAllRanges();
    }
    scroller.scrollLeft = drag.left - dx;
  };

  /** @param {PointerEvent} event */
  #onPointerEnd = (event) => {
    const drag = this.#drag;
    const scroller = this.#scroller;
    if (!drag || !scroller || event.pointerId !== drag.id) return;

    this.#drag = null;
    if (!drag.moved) return;
    delete scroller.dataset.hcDragging;
    if (scroller.hasPointerCapture(drag.id)) scroller.releasePointerCapture(drag.id);
  };

  /** @param {DragEvent} event */
  #onDragStart = (event) => event.preventDefault();
}

if (!customElements.get('hc-size-chart-frame')) {
  customElements.define('hc-size-chart-frame', HcSizeChartFrame);
}
